import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

const AVAILABILITY = [
  { v: "full-time", l: "Full-time (40+ hrs/wk)" },
  { v: "part-time", l: "Part-time (10–30 hrs/wk)" },
  { v: "project-based", l: "Project-based" },
];

const COMMON_LANGS = ["English", "French", "Spanish", "Mandarin", "Hindi", "Tagalog", "Arabic", "Portuguese"];

export default function VirtualProfileEditor() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cats, setCats] = useState({ categories: [], service_types: [] });
  const [form, setForm] = useState({
    headline: "",
    bio: "",
    country: "",
    city: "",
    skills: "",
    hourly_rate: "",
    availability: "part-time",
    languages: ["English"],
    years_experience: "",
    resume_url: "",
    portfolio_url: "",
    linkedin_url: "",
    website_url: "",
    certifications: "",
    services: [],
  });
  const [completion, setCompletion] = useState(0);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/virtual/profile/me");
      if (data) {
        setForm({
          headline: data.headline || "",
          bio: data.bio || "",
          country: data.country || "",
          city: data.city || "",
          skills: (data.skills || []).join(", "),
          hourly_rate: data.hourly_rate || "",
          availability: data.availability || "part-time",
          languages: data.languages || ["English"],
          years_experience: data.years_experience || "",
          resume_url: data.resume_url || "",
          portfolio_url: data.portfolio_url || "",
          linkedin_url: data.linkedin_url || "",
          website_url: data.website_url || "",
          certifications: (data.certifications || []).join(", "),
          services: data.services || [],
        });
        setCompletion(data.profile_completion || 0);
      }
    } catch (err) {
      // 404 = no profile yet (expected on first load). Anything else is unexpected.
      if (err?.response?.status !== 404) {
        console.error("Failed to load freelancer profile:", err);
      }
    }
  }, []);

  useEffect(() => {
    if (!user) { navigate("/login?redirect=/virtual/freelancer/profile"); return; }
    api.get("/virtual/categories").then((r) => setCats(r.data)).catch(() => {});
    load();
  }, [user, navigate, load]);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const toggleLang = (lang) => {
    setForm((f) => ({ ...f, languages: f.languages.includes(lang) ? f.languages.filter((l) => l !== lang) : [...f.languages, lang] }));
  };
  const toggleService = (svc) => {
    setForm((f) => ({ ...f, services: f.services.includes(svc) ? f.services.filter((s) => s !== svc) : [...f.services, svc] }));
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.headline || !form.bio || !form.country) {
      toast.error("Headline, bio and country are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        hourly_rate: parseFloat(form.hourly_rate) || 0,
        years_experience: parseInt(form.years_experience, 10) || 0,
        skills: form.skills.split(",").map((s) => s.trim()).filter(Boolean),
        certifications: form.certifications.split(",").map((s) => s.trim()).filter(Boolean),
      };
      const { data } = await api.put("/virtual/profile/me", payload);
      setCompletion(data.profile_completion || 0);
      toast.success("Profile saved!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <section className="pt-24 pb-16 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/virtual" className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 hover:text-slate-900">← Hire Virtual Staff</Link>
        <h1 className="font-display text-4xl mt-2 mb-2">Your freelancer profile</h1>
        <p className="text-sm text-slate-600 mb-6">A complete profile gets 3× more invites. Fill everything you can.</p>

        {/* Completion bar */}
        <div className="mb-8" data-testid="profile-completion">
          <div className="flex items-center justify-between text-xs uppercase tracking-wider font-semibold mb-2">
            <span className="text-slate-700">Profile completion</span>
            <span className={completion >= 80 ? "text-green-600" : "text-accent-red"}>{completion}%</span>
          </div>
          <div className="h-2 bg-slate-100 overflow-hidden">
            <div className="h-full bg-brand transition-all" style={{ width: `${completion}%` }} />
          </div>
        </div>

        <form onSubmit={save} className="space-y-5" data-testid="virtual-profile-form">
          <label className="block">
            <span className="text-xs uppercase tracking-wider font-semibold text-slate-700">Headline *</span>
            <input value={form.headline} onChange={upd("headline")} required maxLength={100} placeholder="e.g. Shopify developer & conversion specialist" className="mt-1 w-full px-3 py-3 border border-slate-300" data-testid="vp-headline" />
          </label>

          <label className="block">
            <span className="text-xs uppercase tracking-wider font-semibold text-slate-700">Bio *</span>
            <textarea value={form.bio} onChange={upd("bio")} required rows={5} placeholder="Brief intro: your skills, results, who you've worked with." className="mt-1 w-full px-3 py-3 border border-slate-300" data-testid="vp-bio" />
          </label>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-xs uppercase tracking-wider font-semibold text-slate-700">Country *</span>
              <input value={form.country} onChange={upd("country")} required placeholder="Canada" className="mt-1 w-full px-3 py-3 border border-slate-300" data-testid="vp-country" />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-wider font-semibold text-slate-700">City</span>
              <input value={form.city} onChange={upd("city")} placeholder="Toronto" className="mt-1 w-full px-3 py-3 border border-slate-300" data-testid="vp-city" />
            </label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <label className="block">
              <span className="text-xs uppercase tracking-wider font-semibold text-slate-700">Hourly rate (CAD) *</span>
              <input type="number" min="0" value={form.hourly_rate} onChange={upd("hourly_rate")} required placeholder="35" className="mt-1 w-full px-3 py-3 border border-slate-300" data-testid="vp-rate" />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-wider font-semibold text-slate-700">Years experience</span>
              <input type="number" min="0" value={form.years_experience} onChange={upd("years_experience")} placeholder="5" className="mt-1 w-full px-3 py-3 border border-slate-300" data-testid="vp-experience" />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-wider font-semibold text-slate-700">Availability</span>
              <select value={form.availability} onChange={upd("availability")} className="mt-1 w-full px-3 py-3 border border-slate-300" data-testid="vp-availability">
                {AVAILABILITY.map((a) => <option key={a.v} value={a.v}>{a.l}</option>)}
              </select>
            </label>
          </div>

          <div>
            <span className="text-xs uppercase tracking-wider font-semibold text-slate-700">Services offered</span>
            <div className="mt-2 flex flex-wrap gap-2" data-testid="vp-services">
              {cats.service_types.map((s) => (
                <button type="button" key={s} onClick={() => toggleService(s)} className={`text-xs px-3 py-1.5 border ${form.services.includes(s) ? "bg-brand text-white border-brand" : "border-slate-300 text-slate-700 hover:bg-slate-50"}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>

          <div>
            <span className="text-xs uppercase tracking-wider font-semibold text-slate-700">Languages</span>
            <div className="mt-2 flex flex-wrap gap-2" data-testid="vp-languages">
              {COMMON_LANGS.map((l) => (
                <button type="button" key={l} onClick={() => toggleLang(l)} className={`text-xs px-3 py-1.5 border ${form.languages.includes(l) ? "bg-brand text-white border-brand" : "border-slate-300 text-slate-700 hover:bg-slate-50"}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>

          <label className="block">
            <span className="text-xs uppercase tracking-wider font-semibold text-slate-700">Skills (comma-separated)</span>
            <input value={form.skills} onChange={upd("skills")} placeholder="Shopify, Liquid, JavaScript, SEO" className="mt-1 w-full px-3 py-3 border border-slate-300" data-testid="vp-skills" />
          </label>

          <label className="block">
            <span className="text-xs uppercase tracking-wider font-semibold text-slate-700">Certifications (comma-separated)</span>
            <input value={form.certifications} onChange={upd("certifications")} placeholder="Google Ads Cert, Shopify Partner" className="mt-1 w-full px-3 py-3 border border-slate-300" data-testid="vp-certifications" />
          </label>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-xs uppercase tracking-wider font-semibold text-slate-700">Resume URL</span>
              <input type="url" value={form.resume_url} onChange={upd("resume_url")} placeholder="https://…" className="mt-1 w-full px-3 py-3 border border-slate-300" data-testid="vp-resume" />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-wider font-semibold text-slate-700">Portfolio URL</span>
              <input type="url" value={form.portfolio_url} onChange={upd("portfolio_url")} placeholder="https://…" className="mt-1 w-full px-3 py-3 border border-slate-300" data-testid="vp-portfolio" />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-wider font-semibold text-slate-700">LinkedIn URL</span>
              <input type="url" value={form.linkedin_url} onChange={upd("linkedin_url")} placeholder="https://linkedin.com/in/…" className="mt-1 w-full px-3 py-3 border border-slate-300" data-testid="vp-linkedin" />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-wider font-semibold text-slate-700">Personal website</span>
              <input type="url" value={form.website_url} onChange={upd("website_url")} placeholder="https://…" className="mt-1 w-full px-3 py-3 border border-slate-300" data-testid="vp-website" />
            </label>
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-slate-200">
            <Link to="/virtual" className="text-sm text-slate-500 hover:text-slate-900">Cancel</Link>
            <button type="submit" disabled={saving} className="bg-brand text-white px-6 py-3 font-semibold hover:bg-[#122148] disabled:opacity-50" data-testid="vp-save">
              {saving ? "Saving…" : "Save profile"}
            </button>
          </div>
        </form>
      </section>
      <Footer />
    </div>
  );
}
